// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import {
  initTimeTravel,
  isTimeTravelActive,
  realTimeOffset,
} from './lib/timekeeper';
import { SESSION_END_MARKER } from './lib/sessionEndCause';

// Runs before any application module evaluates, so the clock is in place before anything can
// capture the real one. TIME_TRAVEL development builds default to the moment the seed data reads
// best; an explicit `?timeTravel=` outranks it.
//
// Only development and e2e builds travel at all. A simulated clock is a testing affordance, and
// letting a `?timeTravel=` link move the clock of a deployed app would silently misdate everything
// a user sees. The literal `process.env.APP_ENV` text is what Next replaces at build time, so a
// production build folds this to `false` and never reaches initTimeTravel.
if (process.env.APP_ENV === 'development' || process.env.APP_ENV === 'e2e') {
  initTimeTravel(
    process.env.APP_ENV === 'development' && process.env.TIME_TRAVEL
      ? new Date('2022-07-20T07:00:00.000Z')
      : undefined,
  );
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1.0,
  enabled:
    process.env.APP_ENV === 'production' ||
    process.env.APP_ENV === 'staging' ||
    process.env.APP_ENV === 'experimental',
  environment: process.env.APP_ENV || 'development',
  normalizeDepth: 5,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['warn', 'error', 'debug', 'assert'],
    }),
    Sentry.browserTracingIntegration({}),
  ],
  // Time travel patches the global Date, so an event from such a session would be stamped at the
  // simulated instant and land outside Sentry's ingest window. Shift every timestamp on the event
  // by the same offset — keeping the breadcrumb trail's spacing — and tag the simulated instant, so
  // these reports stay findable instead of being dropped or misfiled.
  beforeSend(event) {
    if (!isTimeTravelActive()) return event;

    const offsetSeconds = realTimeOffset() / 1000;
    if (event.timestamp !== undefined) event.timestamp += offsetSeconds;
    event.breadcrumbs = event.breadcrumbs?.map((crumb) =>
      crumb.timestamp === undefined
        ? crumb
        : { ...crumb, timestamp: crumb.timestamp + offsetSeconds },
    );
    event.tags = { ...event.tags, timeTravel: new Date().toISOString() };
    return event;
  },
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    // Next.js throws this when a route transition is interrupted by another
    // navigation. Intentional router behavior, not an application error.
    'Cancel rendering route',
    // Session ends are diagnostics recorded on purpose, not errors.
    SESSION_END_MARKER,
  ],
  // ...
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps
});
